import { reactive, computed } from 'vue';
import {
  arrayRemove, collection, deleteField, doc, onSnapshot, query, serverTimestamp,
  setDoc, updateDoc, where,
} from 'firebase/firestore';
import { projectFirestore } from '@/firebase/config';
import dataHealthStore from '@/stores/dataHealthStore';
import {
  badgeLabel, isMuted, postedTotal, totalUnread, unreadFor,
} from '@/utils/commsUnread';
import { newDocumentId } from '@/utils/documentId';
import { dmConversationId, normaliseMemberIds } from '../../shared/comms-conversations/index.js';

// ███ UNREAD STATE, APP-WIDE ███
//
// T3 (2026-10-27, brief-comms-chat).
//
// ⚠ WHY THIS IS A STORE AND NOT STATE INSIDE `CommsChat.vue`. The nav badge has
// to show a number while the chat is CLOSED, and the chat is the only thing that
// held the conversation listeners. Reading them again from the nav would be a
// second pair of listeners on the same two queries — on every screen in the app,
// for every member, permanently. So the listeners moved here, the chat reads
// them from here, and the badge adds nothing.
//
// THREE LISTENERS IN TOTAL, and the ruling's "one listener each" is what the
// count is measured against:
//
//   1. conversations where `audience == 'everyone'`
//   2. conversations where `memberIds array-contains uid`
//   3. this viewer's own `conversationReads` collection
//
// The first two are the T0 union and are one logical list — Firestore proves a
// list against the query's own constraints, so a disjunctive read rule cannot be
// served by a single query. The third is the whole reason the marker is a
// subcollection: one listener covers every conversation, whereas a marker map on
// the person document would have re-delivered that document to every colleague
// holding one of the seven org-wide `people` listeners.
//
// ⚠ `start` IS IDEMPOTENT, and that is load-bearing rather than defensive. Both
// `Navbar.vue` and `CommsChat.vue` call it — the nav because the badge must work
// with the chat closed, the chat because it must work if the nav is not mounted.
// Without the guard, opening the chat would double every listener.
//
// ⚠ A DENIED LISTENER HERE IS TERMINAL AND SILENT, which is the failure this
// repository has already paid for once. A dead reads listener does not empty the
// badge noisily; it freezes it at whatever it last said, which is
// indistinguishable from "nothing new". So each failure is reported to
// `dataHealthStore`, which is the app-wide surface built for exactly this, and
// the list's own sentence stays the chat's business.
//
// Plain reactive object, not Pinia, and NOT persisted — matching
// `notificationStore.js` and `dataHealthStore.js`. Persisting it would revive a
// stale badge on a fresh boot and show a count for messages already read on
// another device.

// ███ THE SENTENCES THIS STORE PUTS ON A SCREEN ███
//
// Exported rather than inlined so there is ONE definition of each and the copy
// scanner can find them. `LIST_FAILED` moved here from `CommsChat.vue` when the
// listeners did: the component that renders it no longer owns the failure.
export const LIST_FAILED = 'Your conversations could not be loaded. Try again in a moment.';
export const READS_FAILED = 'Your read markers could not be loaded.';

const state = reactive({
  organisationId: '',
  uid: '',
  // Kept apart rather than merged on write: a snapshot from one query replaces
  // only its own list, and merging on write would mean a snapshot from the
  // `everyone` query could not tell which of the merged entries were its own to
  // remove when a conversation stops matching it.
  everyone: [],
  member: [],
  // convId -> { lastReadAt, readTotal, mute }
  markers: {},
  // Set when either conversation listener dies. The chat renders it; the nav
  // deliberately does not, because a nav badge is not a place to put a sentence.
  listError: '',
  readsError: '',
  // ⚠ ONE FLAG PER QUERY, BECAUSE AN EMPTY LIST IS NOT A LOADED LIST (T7).
  // The Dashboard's Latest-news card is re-fed from these conversations, and
  // every card on that screen obeys the same rule: it may only say "nothing
  // here" once it knows. Before this the only readers were the nav badge,
  // which shows nothing at zero anyway, and the chat, which has its own
  // loading state — so "still arriving" and "genuinely none" were the same
  // value and nobody could tell them apart.
  everyoneLoaded: false,
  memberLoaded: false,
});

let stopEveryone = null;
let stopMember = null;
let stopReads = null;

const conversations = computed(() => {
  const byId = new Map();
  for (const c of [...state.everyone, ...state.member]) {
    if (c && typeof c.id === 'string') byId.set(c.id, c);
  }
  return [...byId.values()];
});

const markersById = computed(() => state.markers);

/** Both conversation queries have reported — settled, not necessarily non-empty. */
const listLoaded = computed(() => state.everyoneLoaded && state.memberLoaded);

// The nav badge's number and the label it renders, both from `commsUnread.js` so
// the nav and the row cannot disagree about what muted means.
const total = computed(() => totalUnread(conversations.value, state.markers));
const totalLabel = computed(() => badgeLabel(total.value));

const conversationById = (convId) => conversations.value.find((c) => c.id === convId) || null;

const unreadIn = (convId) => unreadFor(conversationById(convId), state.markers[convId]);
const mutedIn = (convId) => isMuted(state.markers[convId]);

const collect = (key) => (snapshot) => {
  const next = [];
  snapshot.forEach((d) => next.push({ id: d.id, ...d.data() }));
  state[key] = next;
  // Set AFTER the list, so a reader woken by this assignment never sees
  // "loaded" beside the previous snapshot's rows.
  state[`${key}Loaded`] = true;
};

const onListError = (which) => (error) => {
  state.listError = LIST_FAILED;
  // ⚠ A DEAD LISTENER COUNTS AS SETTLED. A denied `onSnapshot` is TERMINAL in
  // this SDK — measured, and recorded in memory/firestore-sdk-denied-read-behaviour.md
  // — so it never calls back again. Leaving the flag false would hold every
  // reader in "still loading" for ever, which is the one state that shows
  // neither the rows nor the sentence that says they could not be loaded.
  state[`${which}Loaded`] = true;
  dataHealthStore.reportFailure(`conversations (${which})`, error);
};

const start = ({ organisationId, uid }) => {
  if (!organisationId || !uid) return false;
  // Already running for this viewer: the second caller is a no-op, not a
  // second set of listeners.
  if (stopEveryone && state.organisationId === organisationId && state.uid === uid) return false;
  // A different viewer or a different organisation: tear the old ones down
  // first, or the previous tenant's conversations stay in the union.
  if (stopEveryone || stopMember || stopReads) stop();

  state.organisationId = organisationId;
  state.uid = uid;

  const base = collection(projectFirestore, `organisations/${organisationId}/conversations`);
  stopEveryone = onSnapshot(
    query(base, where('audience', '==', 'everyone')),
    collect('everyone'),
    onListError('everyone'),
  );
  stopMember = onSnapshot(
    query(base, where('memberIds', 'array-contains', uid)),
    collect('member'),
    onListError('members'),
  );

  stopReads = onSnapshot(
    collection(projectFirestore, `organisations/${organisationId}/people/${uid}/conversationReads`),
    (snapshot) => {
      const next = {};
      snapshot.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      // Replaced wholesale rather than merged: a marker is never deleted by the
      // rules, so a key that has left the snapshot has left the collection, and
      // keeping it would hold a stale `readTotal` against a live conversation.
      state.markers = next;
      state.readsError = '';
    },
    (error) => {
      // ⚠ NOT CLEARED. A dead marker listener leaves `markers` as it was, which
      // over-states how much is read rather than under-stating it; emptying it
      // would flip every conversation to fully unread and put a badge on the nav
      // that no amount of reading can clear.
      state.readsError = READS_FAILED;
      dataHealthStore.reportFailure('conversationReads', error);
    },
  );
  return true;
};

function stop() {
  if (stopEveryone) stopEveryone();
  if (stopMember) stopMember();
  if (stopReads) stopReads();
  stopEveryone = null;
  stopMember = null;
  stopReads = null;
  state.organisationId = '';
  state.uid = '';
  state.everyone = [];
  state.member = [];
  state.markers = {};
  state.listError = '';
  state.readsError = '';
  state.everyoneLoaded = false;
  state.memberLoaded = false;
}

const markerRef = (convId) => doc(
  projectFirestore,
  `organisations/${state.organisationId}/people/${state.uid}/conversationReads/${convId}`,
);

// Mark a conversation read up to everything counted so far.
//
// ⚠ `readTotal` IS THE TOTAL THIS CLIENT CAN SEE, not a count of rendered
// messages. If a message lands between the snapshot and this write, the total
// rises again and the conversation goes back to unread by one — which is the
// safe direction. Writing a rendered-message count instead would claim to have
// read a message that was never counted.
//
// ⚠ `{ merge: true }` IS REQUIRED, not preferred: a non-merge write would drop
// `mute`, silently unmuting a conversation every time it is opened. The rule
// refuses a write that drops `lastReadAt` for the same class of reason.
const markRead = async (convId) => {
  if (!state.organisationId || !state.uid || !convId) return false;
  const conversation = conversationById(convId);
  await setDoc(markerRef(convId), {
    lastReadAt: serverTimestamp(),
    readTotal: postedTotal(conversation),
  }, { merge: true });
  return true;
};

// ⚠ NO `lastReadAt` HERE, which is the whole point of the fields being
// independent in the rule. Muting writes one boolean; if it also wrote the
// marker it would clear the unread it was asked to silence, and unmuting would
// then look like it had lost messages.
const setMute = async (convId, muted) => {
  if (!state.organisationId || !state.uid || !convId) return false;
  await setDoc(markerRef(convId), { mute: muted === true }, { merge: true });
  return true;
};

// ███ T4: CREATING AND CURATING A CONVERSATION ███
//
// These sit here rather than in a store of their own because this module
// already owns the conversation listeners and the Firestore handle. Its NAME is
// now narrower than its job — it is the comms store, not the unread store — and
// that is recorded rather than fixed in the same change as a feature.
//
// ⚠ EVERY ONE OF THEM IS A DIRECT CLIENT WRITE, which `firestore.rules`
// permits and gates. There is no Cloud Function for conversation creation, so
// the rules are the ONLY gate: a manager check here is a convenience for the
// reader, never the enforcement. `callerIsManager` in the rules is what actually
// refuses a non-manager's channel.

const conversationRef = (convId) => doc(
  projectFirestore, `organisations/${state.organisationId}/conversations`, convId,
);

const ready = (convId) => Boolean(state.organisationId) && Boolean(state.uid)
  && (convId === undefined || Boolean(convId));

/**
 * Create a channel. Returns its id, or '' when the store has no viewer yet.
 *
 * ⚠ `memberIds` IS OMITTED ENTIRELY FOR AN `everyone` CHANNEL, not written as
 * `[]`. The rules forbid the key on such a document, so an empty array is a
 * denial rather than a harmless default — and the reason the rules forbid it is
 * that a member list nobody reads is one that drifts for ever.
 */
const createChannel = async ({ name, audience, memberIds }) => {
  if (!ready()) return '';
  const convId = newDocumentId();
  const body = {
    type: 'channel',
    name,
    audience,
    createdBy: state.uid,
    createdAt: serverTimestamp(),
    archived: false,
  };
  if (audience === 'members') {
    const members = normaliseMemberIds(memberIds);
    if (!members) return '';
    body.memberIds = members;
  }
  await setDoc(conversationRef(convId), body);
  return convId;
};

/**
 * Create a DM at its derived id. Returns the id.
 *
 * ⚠ THE NAME IS EMPTY AND THE LIST DOES NOT USE IT. The rules require a
 * non-empty `name` on every conversation, so a DM is named for the rules'
 * benefit and the list renders its participants instead; writing the
 * participants INTO the name would be a stored derived value that goes stale the
 * moment somebody leaves or is renamed.
 */
const createDm = async ({ memberIds }) => {
  if (!ready()) return '';
  const members = normaliseMemberIds(memberIds);
  if (!members || !members.includes(state.uid)) return '';
  const convId = dmConversationId(members);
  if (!convId) return '';
  await setDoc(conversationRef(convId), {
    type: 'dm',
    name: 'Direct message',
    audience: 'members',
    memberIds: members,
    createdBy: state.uid,
    createdAt: serverTimestamp(),
    archived: false,
  });
  return convId;
};

const renameConversation = async (convId, name) => {
  if (!ready(convId)) return false;
  await updateDoc(conversationRef(convId), { name });
  return true;
};

const setMembers = async (convId, memberIds) => {
  if (!ready(convId)) return false;
  const members = normaliseMemberIds(memberIds);
  if (!members) return false;
  await updateDoc(conversationRef(convId), { memberIds: members });
  return true;
};

/**
 * Switch a channel between `everyone` and `members`, in either direction.
 *
 * ⚠ ONE WRITE, AND `deleteField()` IS WHAT MAKES WIDENING LEGAL. The rules check
 * the conversation's shape against the MERGED post-write document, and that
 * shape forbids `memberIds` on an `everyone` conversation — so widening must
 * REMOVE the key in the same update that changes the audience. Writing `null`,
 * or leaving the key in place, is refused; splitting it into two updates is
 * refused at the first half. Both directions are measured in
 * `src/tests/rules/conversations.rules.test.js`.
 */
const setAudience = async (convId, { audience, memberIds }) => {
  if (!ready(convId)) return false;
  if (audience === 'everyone') {
    await updateDoc(conversationRef(convId), { audience: 'everyone', memberIds: deleteField() });
    return true;
  }
  const members = normaliseMemberIds(memberIds);
  if (!members) return false;
  await updateDoc(conversationRef(convId), { audience: 'members', memberIds: members });
  return true;
};

const setArchived = async (convId, archived) => {
  if (!ready(convId)) return false;
  await updateDoc(conversationRef(convId), { archived: archived === true });
  return true;
};

/**
 * Leave a DM.
 *
 * ⚠ `arrayRemove` RATHER THAN A REWRITTEN LIST, because the rule is a self-only
 * uid delta over the BEFORE document: it permits removing your own uid and
 * nothing else. A client that sent a whole new array would have to reproduce the
 * rest of it exactly, and any staleness in its copy would read as an attempt to
 * remove somebody else.
 */
const leaveDm = async (convId) => {
  if (!ready(convId)) return false;
  await updateDoc(conversationRef(convId), { memberIds: arrayRemove(state.uid) });
  return true;
};

export default {
  state,
  conversations,
  listLoaded,
  markersById,
  createChannel,
  createDm,
  renameConversation,
  setMembers,
  setAudience,
  setArchived,
  leaveDm,
  total,
  totalLabel,
  unreadIn,
  mutedIn,
  start,
  stop,
  markRead,
  setMute,
};
